import { isAnalyticsEnabled } from "./analyticsConfig";
import { apiUrl, isMobileBuild } from "../platform/runtime";
import { deleteAnalyticsEvents, peekAnalyticsBatch } from "./analyticsQueueRepo";

let flushActive = false;
let nextAllowedFlushAt = 0;
let retryDelayMs = 2_000;
let retryTimer: number | undefined;

export async function flushAnalyticsQueue(reason: "startup" | "online" | "hidden" | "milestone" = "milestone"): Promise<void> {
  if (!isAnalyticsEnabled() || flushActive || !navigator.onLine || Date.now() < nextAllowedFlushAt) {
    return;
  }
  flushActive = true;
  try {
    const batch = await peekAnalyticsBatch();
    if (!batch.length) {
      retryDelayMs = 2_000;
      return;
    }
    const body = JSON.stringify({ events: batch.map((record) => record.event) });
    const eventIds = batch.map((record) => record.eventId);

    if (!isMobileBuild() && reason === "hidden" && navigator.sendBeacon) {
      const sent = navigator.sendBeacon(apiUrl("/api/analytics-events"), new Blob([body], { type: "application/json" }));
      if (sent) {
        await deleteAnalyticsEvents(eventIds);
        retryDelayMs = 2_000;
        return;
      }
    }

    const response = await fetch(apiUrl("/api/analytics-events"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body,
      credentials: "same-origin",
      keepalive: reason === "hidden"
    });
    if (!response.ok) {
      throw new Error("Analytics flush failed.");
    }
    await deleteAnalyticsEvents(eventIds);
    retryDelayMs = 2_000;
  } catch {
    scheduleRetry();
  } finally {
    flushActive = false;
  }
}

function scheduleRetry(): void {
  const jitter = Math.floor(Math.random() * 800);
  const delay = Math.min(60_000, retryDelayMs + jitter);
  nextAllowedFlushAt = Date.now() + delay;
  retryDelayMs = Math.min(60_000, retryDelayMs * 2);
  if (retryTimer) {
    window.clearTimeout(retryTimer);
  }
  retryTimer = window.setTimeout(() => {
    retryTimer = undefined;
    void flushAnalyticsQueue("milestone");
  }, delay);
}
